import {
  emptyAnnotation,
  isBlank,
  withDefaults,
  type Annotation,
  type AnnotationStore,
  type SharedAnnotation,
  type SharedSource,
  type StorageMode,
  type Vote,
} from './types.ts'

/**
 * The local-first annotation store.
 *
 * This is the authoritative read path for the whole app, signed in or not. Sign-in
 * adds synchronisation on top (see store/sync.ts); it never replaces this. A user
 * standing in the park with no signal must still see their own notes, which is the
 * one condition the app is guaranteed to be used in.
 */

const VERSION = 1
export const STORAGE_KEY = `oya-annotated:annotations:v${VERSION}`

const SHARED_VERSION = 1

/**
 * Other people's annotations live under their own key, deliberately.
 *
 * `persist()` answers a refused write by flipping the whole store to `ephemeral`, which
 * `NoteEditor` renders as "notes are not being saved on this device" — a statement about
 * what the *reader* wrote. Folding a friend's data into that payload would let someone
 * else's note trigger that warning, and let one oversized shared snapshot take the
 * reader's own annotations down with it. Two keys, two failure domains (KTD16).
 */
export const SHARED_STORAGE_KEY = `oya-annotated:shared:v${SHARED_VERSION}`

/**
 * Every source, in the order they fold together.
 *
 * The order is the tie-break: when the same document arrives through both read paths
 * with the same `updatedAt` — the normal case for a friend who also shared explicitly —
 * the earlier source's copy is the one kept, so the deduped result does not depend on
 * which listener happened to fire last.
 */
const SHARED_SOURCES: readonly SharedSource[] = ['shared', 'visible']

/**
 * The answer for every concert nobody else has annotated, which is most of them.
 *
 * One instance for the whole module, because the identity is the point: the day view
 * asks about 22 concerts on every minute tick and hands each answer to a memoised row.
 */
const NO_SHARED: SharedAnnotation[] = []

/** The subset of the Storage API this store needs, so tests can supply their own. */
export interface StorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

interface Persisted {
  version: number
  items: Record<string, Annotation>
  /**
   * Concert ids whose annotation was cleared, with the time it happened.
   *
   * Deletion is expressed locally as absence, which the sync layer cannot see —
   * it only uploads what is present. Without a tombstone a cleared vote is never
   * deleted from Firestore and the next snapshot resurrects it.
   */
  tombstones?: Record<string, string>
}

/** The shared map's own payload, under its own key. */
interface PersistedShared {
  version: number
  sources: Partial<Record<SharedSource, SharedAnnotation[]>>
}

export interface CreateStoreOptions {
  /** Defaults to `window.localStorage` when available. */
  storage?: StorageLike | null
  /** Injectable clock, for tests. */
  now?: () => Date
}

/**
 * Resolves two versions of the same annotation. Last write wins on `updatedAt`.
 *
 * `sharedWith` is the exception: only the server writes it, so the remote value is
 * authoritative even when the local copy is newer. Without that carve-out, a local
 * edit made after a share would silently revoke the share.
 */
export function mergeByUpdatedAt(
  local: Annotation | null,
  remote: Annotation | null,
): Annotation {
  if (!local) return withDefaults(remote as Annotation)
  if (!remote) return withDefaults(local)
  const winner = remote.updatedAt > local.updatedAt ? remote : local
  return withDefaults({ ...winner, sharedWith: remote.sharedWith ?? winner.sharedWith })
}

export function defaultStorage(): StorageLike | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    // Accessing localStorage can itself throw when cookies are blocked.
    return null
  }
}

function isAnnotation(value: unknown): value is Annotation {
  if (typeof value !== 'object' || value === null) return false
  const a = value as Record<string, unknown>
  return typeof a['concertId'] === 'string' && typeof a['note'] === 'string'
}

/**
 * Validates one stored annotation belonging to someone else.
 *
 * `ownerName` must be *present* and either a name or `null` — not merely a string. A
 * friend's annotation with no display name is a real row that the cloud layer keeps and
 * the views render with a fallback prefix (R14), so requiring a string here would drop
 * on reload exactly the rows the read path went out of its way to preserve, and the
 * friend's note would vanish on the first cold start with no network.
 */
function isSharedAnnotation(value: unknown): value is SharedAnnotation {
  if (!isAnnotation(value)) return false
  const name = (value as SharedAnnotation).ownerName
  return typeof name === 'string' || name === null
}

/**
 * Whether two annotations are the same in every field this app persists.
 *
 * Used to answer "did this snapshot actually change anything?" — `updatedAt` alone is
 * not enough, because `mergeByUpdatedAt` can take `sharedWith` from the remote side
 * even when the local copy is newer.
 */
function sameAnnotation(a: Annotation, b: Annotation): boolean {
  return (
    a.concertId === b.concertId &&
    a.uid === b.uid &&
    a.ownerName === b.ownerName &&
    a.vote === b.vote &&
    a.note === b.note &&
    a.dice === b.dice &&
    a.updatedAt === b.updatedAt &&
    a.sharedWith.length === b.sharedWith.length &&
    a.sharedWith.every((uid, i) => uid === b.sharedWith[i])
  )
}

/**
 * The dedup key: one person's annotation of one concert, however it reached us.
 *
 * Joined on a character neither half can contain, so no two distinct pairs collapse
 * into one key.
 */
export function sharedKey(annotation: SharedAnnotation): string {
  return `${annotation.uid ?? ''}\u0000${annotation.concertId}`
}

export interface LocalAnnotationStore extends AnnotationStore {
  /**
   * Monotonic write counter. React binds to this via `useSyncExternalStore`: a
   * number is referentially stable, whereas returning a freshly-built array from
   * a snapshot getter would re-render forever.
   */
  getVersion(): number
  /**
   * Replaces everything one source has delivered.
   *
   * Per source, and never across sources: each is one live Firestore listener that
   * hands over a complete snapshot of its own query, and knows nothing about the
   * other's rows. A single set would make every snapshot look like the other
   * listener's annotations had been revoked.
   */
  replaceSharedFrom(source: SharedSource, shared: readonly SharedAnnotation[]): void
  /**
   * What one source last delivered, undeduped.
   *
   * The friends page reads `sharedFrom('shared')` because it is the only place an
   * explicit share is still distinguishable from the friend feed (R34) — once a friend
   * is accepted, `sharedAnnotations` folds both into one deduped list, and the
   * deliberate act of sending someone a concert would otherwise disappear among
   * everything friendship exposes. It doubles as the seam the store's own tests use to
   * assert that one listener's snapshot left the other's alone, and that `clear()`
   * reached every source rather than only the one a union happens to be empty of.
   */
  sharedFrom(source: SharedSource): SharedAnnotation[]
  /** Applies a remote annotation, merged by `updatedAt`. Used by the sync layer. */
  applyRemote(annotation: Annotation): void
  /**
   * Sets who the annotations belong to, and stamps the existing ones.
   *
   * Every subsequent write carries this uid too — without that, an annotation made
   * *after* sign-in would still be written with `uid: null` and the sync layer,
   * which only uploads the signed-in user's own rows, would silently never send it.
   */
  setOwner(uid: string | null, ownerName?: string | null): void
  /** Concert ids whose annotation was cleared and not yet deleted remotely. */
  pendingDeletions(): string[]
  /** Marks a tombstone as propagated. */
  clearTombstone(concertId: string): void
  /** Wipes local state — sign-out, and the "delete my data" control. */
  clear(): void
}

export function createAnnotationStore(options: CreateStoreOptions = {}): LocalAnnotationStore {
  const storage = options.storage === undefined ? defaultStorage() : options.storage
  const now = options.now ?? (() => new Date())

  let items: Record<string, Annotation> = {}
  let tombstones: Record<string, string> = {}
  const sources: Record<SharedSource, SharedAnnotation[]> = { shared: [], visible: [] }
  let mode: StorageMode = storage ? 'persistent' : 'ephemeral'
  const listeners = new Set<() => void>()

  // Load. Anything unreadable is discarded and reported as ephemeral rather than
  // silently claiming a persistence guarantee we are not providing.
  if (storage) {
    const raw = storage.getItem(STORAGE_KEY)
    if (raw !== null) {
      try {
        const parsed = JSON.parse(raw) as Persisted
        if (parsed?.version !== VERSION || typeof parsed.items !== 'object' || parsed.items === null) {
          throw new Error('ukjent skjemaversjon')
        }
        for (const [id, value] of Object.entries(parsed.items)) {
          // Normalise here, not at read time: a record written before a field existed
          // carries `undefined`, which `isBlank` never matches and Firestore refuses to
          // accept — so it would neither delete cleanly nor ever upload again.
          if (isAnnotation(value)) items[id] = withDefaults(value)
        }
        if (parsed.tombstones && typeof parsed.tombstones === 'object') {
          tombstones = { ...parsed.tombstones }
        }
      } catch {
        items = {}
        mode = 'ephemeral'
      }
    }
  }

  // Other people's annotations, loaded from their own key with their own catch. This is
  // what makes friends' notes survive a cold start with no network and no Firebase at
  // all (R26, KTD16) — the cloud layer restores them when it reconnects, so anything
  // unreadable here is dropped silently and, unlike `items`, never reported as a
  // persistence failure of the user's own writing.
  if (storage) {
    const raw = storage.getItem(SHARED_STORAGE_KEY)
    if (raw !== null) {
      try {
        const parsed = JSON.parse(raw) as PersistedShared
        const stored = parsed?.sources
        if (parsed?.version !== SHARED_VERSION || typeof stored !== 'object' || stored === null) {
          throw new Error('ukjent skjemaversjon')
        }
        for (const source of SHARED_SOURCES) {
          const list = stored[source]
          if (!Array.isArray(list)) continue
          // Normalised on the way in for the same reason `items` is: a record written
          // before a field existed carries `undefined`, and the shared views read these
          // straight through to the screen.
          sources[source] = list
            .filter(isSharedAnnotation)
            .map((s) => withDefaults(s) as SharedAnnotation)
        }
      } catch {
        for (const source of SHARED_SOURCES) sources[source] = []
      }
    }
  }

  let version = 0

  /**
   * The deduped union, built once per store version rather than once per call.
   *
   * `null` means "not built yet". The day view asks for one concert at a time inside a
   * memo whose dependencies change on every minute tick, so a per-call fold would run
   * the whole union 22 times a minute and hand every row a new array.
   */
  let sharedView: Map<string, SharedAnnotation[]> | null = null
  let allView: Annotation[] | null = null

  function sharedByConcert(): Map<string, SharedAnnotation[]> {
    if (sharedView) return sharedView

    // Insertion order carries the answer: a `Map` keeps a key where it was first set,
    // so replacing an entry with a newer copy from a later source updates the value
    // without moving the row, and the rendered order stays put.
    const winners = new Map<string, SharedAnnotation>()
    for (const source of SHARED_SOURCES) {
      for (const annotation of sources[source]) {
        const key = sharedKey(annotation)
        const existing = winners.get(key)
        if (existing && existing.updatedAt >= annotation.updatedAt) continue
        winners.set(key, annotation)
      }
    }

    const view = new Map<string, SharedAnnotation[]>()
    for (const annotation of winners.values()) {
      const forConcert = view.get(annotation.concertId)
      if (forConcert) forConcert.push(annotation)
      else view.set(annotation.concertId, [annotation])
    }

    sharedView = view
    return view
  }

  function notify() {
    version += 1
    sharedView = null
    allView = null
    for (const listener of listeners) listener()
  }

  function persist() {
    if (!storage) return
    try {
      const payload: Persisted = { version: VERSION, items, tombstones }
      storage.setItem(STORAGE_KEY, JSON.stringify(payload))
    } catch {
      // Safari private mode and full quotas both land here. Keep the in-memory copy
      // so the session still works, but stop promising it will survive a reload.
      mode = 'ephemeral'
    }
  }

  function persistShared() {
    if (!storage) return
    try {
      const payload: PersistedShared = { version: SHARED_VERSION, sources }
      storage.setItem(SHARED_STORAGE_KEY, JSON.stringify(payload))
    } catch {
      // Dropped in silence, and pointedly without touching `mode`: this write failing
      // means a cold start falls back to whatever the listeners re-deliver, which is
      // the behaviour before R26 — it says nothing about the user's own notes, and the
      // UI must not claim otherwise.
    }
  }

  let owner: string | null = null
  let ownerName: string | null = null

  function update(concertId: string, change: (a: Annotation) => Annotation) {
    const current = withDefaults(items[concertId] ?? emptyAnnotation(concertId))
    const next = { ...change(current), uid: owner, ownerName, updatedAt: now().toISOString() }
    if (isBlank(next)) {
      delete items[concertId]
      tombstones[concertId] = next.updatedAt
    } else {
      items[concertId] = next
      delete tombstones[concertId]
    }
    persist()
    notify()
  }

  return {
    get storageMode() {
      return mode
    },

    getVersion() {
      return version
    },

    getAnnotation(concertId) {
      return items[concertId] ?? null
    },

    sharedAnnotations(concertId) {
      return sharedByConcert().get(concertId) ?? NO_SHARED
    },

    /*
     * Cached per store version, for the same reason `sharedByConcert` is: the day view
     * feeds this straight into five `useMemo` dependency arrays, and a fresh array on
     * every call makes every one of them recompute on every render — including the
     * minute tick from `useNow` and every unrelated filter toggle.
     */
    allAnnotations() {
      return (allView ??= Object.values(items))
    },

    setVote(concertId, vote: Vote) {
      update(concertId, (a) => ({ ...a, vote }))
    },

    setNote(concertId, note) {
      update(concertId, (a) => ({ ...a, note }))
    },

    setDice(concertId, dice) {
      update(concertId, (a) => ({ ...a, dice }))
    },

    subscribe(listener) {
      listeners.add(listener)
      return () => void listeners.delete(listener)
    },

    replaceSharedFrom(source, next) {
      sources[source] = [...next]
      persistShared()
      notify()
    },

    sharedFrom(source) {
      return [...sources[source]]
    },

    /*
     * A Firestore snapshot is the whole query result, not a diff — so one edit to one
     * annotation redelivers every annotation the user owns, and this runs once per row.
     * Without the no-op guard each of those rows re-stringifies the entire store into
     * localStorage and re-renders every subscriber, which turns a single friend-accept
     * (it republishes every annotation, and each upload echoes back the full set) into
     * quadratic work. The guard compares the *merged* value, so `mergeByUpdatedAt`'s
     * remote-wins carve-out for `sharedWith` still decides what counts as unchanged.
     */
    applyRemote(remote) {
      // A remote row older than a local clear is a resurrection, not an update.
      const tombstone = tombstones[remote.concertId]
      if (tombstone && remote.updatedAt <= tombstone) return

      const current = items[remote.concertId] ?? null
      const merged = mergeByUpdatedAt(current, remote)
      const hadTombstone = remote.concertId in tombstones

      if (isBlank(merged)) {
        if (!current && !hadTombstone) return
        delete items[merged.concertId]
      } else {
        if (current && !hadTombstone && sameAnnotation(current, merged)) return
        items[merged.concertId] = merged
      }

      delete tombstones[remote.concertId]
      persist()
      notify()
    },

    pendingDeletions() {
      return Object.keys(tombstones)
    },

    clearTombstone(concertId) {
      if (concertId in tombstones) {
        delete tombstones[concertId]
        persist()
      }
    },

    setOwner(uid, name = null) {
      owner = uid
      ownerName = name
      for (const id of Object.keys(items)) {
        const existing = items[id]
        if (existing && (existing.uid !== uid || existing.ownerName !== name)) {
          items[id] = { ...existing, uid, ownerName: name }
        }
      }
      persist()
      notify()
    },

    clear() {
      items = {}
      tombstones = {}
      // Every source, not just the legacy one: this runs on sign-out and on an account
      // switch, and a source left standing would render the previous user's friends'
      // notes on the next user's schedule.
      for (const source of SHARED_SOURCES) sources[source] = []
      owner = null
      ownerName = null
      try {
        storage?.removeItem(STORAGE_KEY)
      } catch {
        mode = 'ephemeral'
      }
      try {
        storage?.removeItem(SHARED_STORAGE_KEY)
      } catch {
        // Nothing to fall back to and nothing to say: the in-memory copy is already
        // gone, and a stale key is re-read only by a build that then overwrites it at
        // the next snapshot. Not a reason to call the user's own notes unsaved.
      }
      notify()
    },
  }
}
